// Performance optimization: Use passive event listeners where possible
const passiveSupported = (() => {
  let passiveSupported = false
  try {
    const options = {
      get passive() {
        passiveSupported = true
        return false
      },
    }
    window.addEventListener("test", null, options)
    window.removeEventListener("test", null, options)
  } catch (err) {
    passiveSupported = false
  }
  return passiveSupported
})()

// Optimized DOM ready function
const ready = (fn) => {
  if (document.readyState !== "loading") {
    fn()
  } else {
    document.addEventListener("DOMContentLoaded", fn)
  }
}

// Main initialization
ready(() => {
  console.log("DOM loaded, initializing components...")

  // Initialize all components with error handling
  const components = [
    { name: "BestSellers", fn: initBestSellersSlider },
    { name: "MaisVendidos", fn: initMaisVendidosSlider },
    { name: "MaioresDescontos", fn: initMaioresDescontosSlider },
    { name: "Discount", fn: initDiscountSlider },
    { name: "ModularSlider", fn: initModularSlider },
    { name: "FAQ", fn: initFAQAccordion },
    { name: "MobileMenu", fn: initMobileMenu },
    { name: "TopBar", fn: initTopBarClose },
    { name: "Countdown", fn: initCountdownTimer },
    { name: "LazyLoading", fn: initLazyLoading },
    { name: "Accessibility", fn: initAccessibility },
    { name: "MobileSearch", fn: initMobileSearch },
  ]

  components.forEach(({ name, fn }) => {
    try {
      fn()
      console.log(`✓ ${name} initialized`)
    } catch (error) {
      console.error(`✗ Error initializing ${name}:`, error)
    }
  })

  console.log("All components initialized")
})

// ====================================
// UTILITY FUNCTIONS (OPTIMIZED)
// ====================================

// Enhanced debounce with immediate option
const debounce = (func, wait, immediate = false) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      timeout = null
      if (!immediate) func.apply(this, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(this, args)
  }
}

// Throttle function for scroll events
const throttle = (func, limit) => {
  let inThrottle
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

// Optimized element selector with caching
const elementCache = new Map()
const getElement = (selector, useCache = true) => {
  if (useCache && elementCache.has(selector)) {
    return elementCache.get(selector)
  }
  const element = document.querySelector(selector)
  if (useCache && element) {
    elementCache.set(selector, element)
  }
  return element
}

// Enhanced slider class for reusability
class OptimizedSlider {
  constructor(config) {
    this.container = getElement(config.container)
    this.prevBtn = getElement(config.prevBtn)
    this.nextBtn = getElement(config.nextBtn)
    this.progressBar = getElement(config.progressBar)
    this.dotsContainer = getElement(config.dotsContainer)

    this.currentIndex = 0
    this.autoPlay = config.autoPlay || false
    this.autoPlayInterval = config.autoPlayInterval || 5000
    this.slidesToShow = config.slidesToShow || 1
    this.infinite = config.infinite || false

    if (!this.container) return

    this.slides = Array.from(this.container.children)
    this.totalSlides = this.slides.length
    this.autoPlayTimer = null

    this.init()
  }

  init() {
    this.setupEventListeners()
    this.updateSlider()
    if (this.autoPlay) this.startAutoPlay()

    window.addEventListener(
      "resize",
      debounce(() => {
        this.updateSlider()
      }, 250),
      passiveSupported ? { passive: true } : false,
    )
  }

  setupEventListeners() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", () => this.prevSlide())
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", () => this.nextSlide())
    }

    this.setupTouchEvents()

    if (this.autoPlay) {
      this.container.addEventListener("mouseenter", () => this.stopAutoPlay())
      this.container.addEventListener("mouseleave", () => this.startAutoPlay())
    }
  }

  setupTouchEvents() {
    let startX = 0
    let startY = 0
    let isDragging = false
    let hasMoved = false

    this.container.addEventListener(
      "touchstart",
      (e) => {
        startX = e.touches[0].clientX
        startY = e.touches[0].clientY
        isDragging = true
        hasMoved = false
        this.stopAutoPlay()
      },
      passiveSupported ? { passive: true } : false,
    )

    this.container.addEventListener("touchmove", (e) => {
      if (!isDragging) return

      const currentX = e.touches[0].clientX
      const currentY = e.touches[0].clientY
      const diffX = Math.abs(currentX - startX)
      const diffY = Math.abs(currentY - startY)

      // Only prevent default if horizontal swipe is more significant than vertical
      if (diffX > diffY && diffX > 10) {
        e.preventDefault()
        hasMoved = true
      }
    })

    this.container.addEventListener("touchend", (e) => {
      if (!isDragging || !hasMoved) {
        isDragging = false
        this.startAutoPlay()
        return
      }

      const endX = e.changedTouches[0].clientX
      const diff = startX - endX

      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.nextSlide()
        } else {
          this.prevSlide()
        }
      }

      isDragging = false
      hasMoved = false
      this.startAutoPlay()
    })

    // Prevent context menu on long press
    this.container.addEventListener("contextmenu", (e) => {
      if (hasMoved) {
        e.preventDefault()
      }
    })
  }

  updateSlider() {
    const slideWidth = 100 / this.slidesToShow
    const maxIndex = Math.max(0, this.totalSlides - this.slidesToShow)

    if (this.currentIndex > maxIndex) {
      this.currentIndex = maxIndex
    }

    const translateX = -(this.currentIndex * slideWidth)
    this.container.style.transform = `translateX(${translateX}%)`

    this.updateButtons(maxIndex)
    this.updateProgressBar()
    this.updateDots()
  }

  updateButtons(maxIndex) {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0
      this.prevBtn.style.opacity = this.currentIndex === 0 ? "0.5" : "1"
    }

    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= maxIndex
      this.nextBtn.style.opacity = this.currentIndex >= maxIndex ? "0.5" : "1"
    }
  }

  updateProgressBar() {
    if (this.progressBar) {
      const progress = ((this.currentIndex + 1) / this.totalSlides) * 100
      this.progressBar.style.width = `${progress}%`
    }
  }

  updateDots() {
    if (this.dotsContainer) {
      const dots = this.dotsContainer.children
      Array.from(dots).forEach((dot, index) => {
        if (index === this.currentIndex) {
          dot.classList.remove("bg-gray-400")
          dot.classList.add("bg-purple-main")
        } else {
          dot.classList.remove("bg-purple-main")
          dot.classList.add("bg-gray-400")
        }
      })
    }
  }

  nextSlide() {
    const maxIndex = Math.max(0, this.totalSlides - this.slidesToShow)

    if (this.currentIndex < maxIndex) {
      this.currentIndex++
      this.updateSlider()
    } else if (this.infinite) {
      this.currentIndex = 0
      this.updateSlider()
    }
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex--
      this.updateSlider()
    } else if (this.infinite) {
      this.currentIndex = Math.max(0, this.totalSlides - this.slidesToShow)
      this.updateSlider()
    }
  }

  startAutoPlay() {
    if (this.autoPlay && !this.autoPlayTimer) {
      this.autoPlayTimer = setInterval(() => {
        this.nextSlide()
      }, this.autoPlayInterval)
    }
  }

  stopAutoPlay() {
    if (this.autoPlayTimer) {
      clearInterval(this.autoPlayTimer)
      this.autoPlayTimer = null
    }
  }

  goToSlide(index) {
    const maxIndex = Math.max(0, this.totalSlides - this.slidesToShow)
    this.currentIndex = Math.max(0, Math.min(index, maxIndex))
    this.updateSlider()
  }

  destroy() {
    this.stopAutoPlay()
  }
}

// ====================================
// COMPONENT INITIALIZERS
// ====================================

function initBestSellersSlider() {
  const container = getElement("#productCarousel")
  if (!container) {
    console.warn("Best sellers slider container not found")
    return
  }

  new OptimizedSlider({
    container: "#productCarousel",
    prevBtn: "#prevBtn",
    nextBtn: "#nextBtn",
    slidesToShow: 1,
  })
}

function initMaisVendidosSlider() {
  const container = getElement("#productCarousel_MaisVendidos")
  if (!container) {
    console.warn("Best sellers slider container not found")
    return
  }

  new OptimizedSlider({
    container: "#productCarousel_MaisVendidos",
    prevBtn: "#prevBtn_MaisVendidos",
    nextBtn: "#nextBtn_MaisVendidos",
    slidesToShow: 1,
  })
}


function initMaioresDescontosSlider() {
  const container = getElement("#productCarousel_MaioresDescontos")
  if (!container) {
    console.warn("Best sellers slider container not found")
    return
  }

  new OptimizedSlider({
    container: "#productCarousel_MaioresDescontos",
    prevBtn: "#prevBtn_MaioresDescontos",
    nextBtn: "#nextBtn_MaioresDescontos",
    slidesToShow: 1,
  })
}

function initDiscountSlider() {
  const container = getElement("#recommendations-slides-container")
  if (!container) {
    console.warn("Discount slider container not found")
    return
  }

  new OptimizedSlider({
    container: "#recommendations-slides-container",
    prevBtn: "#recommendations-prev-btn",
    nextBtn: "#recommendations-next-btn",
    progressBar: "#recommendations-progress-bar",
    autoPlay: true,
    autoPlayInterval: 6000,
    slidesToShow: 1,
  })
}

function initModularSlider() {
  const container = getElement("#modularSlider")
  if (!container) {
    console.warn("Modular slider container not found")
    return
  }

  const slider = new OptimizedSlider({
    container: "#modularSlider",
    prevBtn: "#modularPrevBtn",
    nextBtn: "#modularNextBtn",
    dotsContainer: "#modularDotsContainer",
    slidesToShow: 1,
    autoPlay: true,
    autoPlayInterval: 4000,
  })

  // Create dots for mobile
  const createDots = () => {
    const dotsContainer = getElement("#modularDotsContainer")
    if (!dotsContainer) return

    const totalDots = slider.totalSlides
    dotsContainer.innerHTML = ""

    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement("button")
      dot.className = "dot w-3 h-3 rounded-full bg-gray-400 transition-all duration-300"
      dot.setAttribute("data-slide", i)
      dot.addEventListener("click", () => slider.goToSlide(i))
      dotsContainer.appendChild(dot)
    }
    slider.updateDots()
  }

  createDots()
}

function initFAQAccordion() {
  const triggers = document.querySelectorAll(".accordion-trigger")

  if (triggers.length === 0) {
    console.warn("FAQ accordion triggers not found")
    return
  }

  triggers.forEach((trigger) => {
    trigger.addEventListener("click", function () {
      const targetId = this.getAttribute("data-target")
      const content = document.getElementById(targetId)
      const icon = this.querySelector(".accordion-icon")
      const isExpanded = this.getAttribute("aria-expanded") === "true"

      triggers.forEach((otherTrigger) => {
        if (otherTrigger !== this) {
          const otherTargetId = otherTrigger.getAttribute("data-target")
          const otherContent = document.getElementById(otherTargetId)
          const otherIcon = otherTrigger.querySelector(".accordion-icon")

          otherTrigger.setAttribute("aria-expanded", "false")
          if (otherContent) {
            otherContent.style.maxHeight = "0"
            otherContent.classList.remove("max-h-96")
            otherContent.classList.add("max-h-0")
          }
          if (otherIcon) {
            otherIcon.style.transform = "rotate(0deg)"
          }
        }
      })

      if (isExpanded) {
        this.setAttribute("aria-expanded", "false")
        if (content) {
          content.style.maxHeight = "0"
          content.classList.remove("max-h-96")
          content.classList.add("max-h-0")
        }
        if (icon) {
          icon.style.transform = "rotate(0deg)"
        }
      } else {
        this.setAttribute("aria-expanded", "true")
        if (content) {
          content.style.maxHeight = content.scrollHeight + "px"
          content.classList.remove("max-h-0")
          content.classList.add("max-h-96")
        }
        if (icon) {
          icon.style.transform = "rotate(180deg)"
        }
      }
    })

    trigger.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        this.click()
      }
    })
  })
}

function initMobileMenu() {
  const hamburger = getElement("#hamburger")
  const mobileNav = getElement("#mobile-nav")
  const closeMobileNav = getElement("#close-mobile-nav")
  const mobileProducts = getElement("#mobile-products")
  const mobileSubmenu = getElement("#mobile-submenu")

  const toggleMenu = (show) => {
    if (show) {
      mobileNav.classList.remove("hidden")
      hamburger.setAttribute("aria-expanded", "true")
      document.body.style.overflow = "hidden"
    } else {
      mobileNav.classList.add("hidden")
      hamburger.setAttribute("aria-expanded", "false")
      document.body.style.overflow = ""
    }
  }

  if (hamburger && mobileNav) {
    hamburger.addEventListener("click", () => {
      const isExpanded = hamburger.getAttribute("aria-expanded") === "true"
      toggleMenu(!isExpanded)
    })
  }

  if (closeMobileNav) {
    closeMobileNav.addEventListener("click", () => toggleMenu(false))
  }

  if (mobileProducts && mobileSubmenu) {
    mobileProducts.addEventListener("click", function () {
      const icon = this.querySelector("svg")
      const isExpanded = this.getAttribute("aria-expanded") === "true"

      this.setAttribute("aria-expanded", !isExpanded)

      if (isExpanded) {
        mobileSubmenu.classList.add("hidden")
        if (icon) icon.style.transform = "rotate(0deg)"
      } else {
        mobileSubmenu.classList.remove("hidden")
        if (icon) icon.style.transform = "rotate(180deg)"
      }
    })
  }
}

function initTopBarClose() {
  const closeBtn = getElement("#close-top-bar")
  const topBar = getElement("#top-bar")

  if (closeBtn && topBar) {
    closeBtn.addEventListener("click", () => {
      topBar.style.display = "none"
    })
  }
}

function initCountdownTimer() {
  const timer = getElement("#countdownTimer")
  const banner = getElement("#discountBanner")
  const closeBtn = getElement("#closeBannerBtn")
  const copyBtn = getElement("#copyCouponBtn")

  if (!timer) {
    console.warn("Countdown timer element not found")
    return
  }

  const endTime = new Date().getTime() + 24 * 60 * 60 * 1000

  function updateTimer() {
    const now = new Date().getTime()
    const distance = endTime - now

    if (distance < 0) {
      timer.textContent = "00:00:00"
      return
    }

    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((distance % (1000 * 60)) / 1000)

    timer.textContent = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
  }

  const timerInterval = setInterval(updateTimer, 1000)
  updateTimer()

  if (closeBtn && banner) {
    closeBtn.addEventListener("click", () => {
      banner.style.display = "none"
      clearInterval(timerInterval)
    })
  }

  if (copyBtn) {
    copyBtn.addEventListener("click", function () {
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText("SOFA30")
          .then(() => {
            showCopyFeedback(this)
          })
          .catch(() => {
            fallbackCopyTextToClipboard("SOFA30", this)
          })
      } else {
        fallbackCopyTextToClipboard("SOFA30", this)
      }
    })
  }

  function showCopyFeedback(button) {
    const originalText = button.textContent
    button.textContent = "Copiado!"
    button.classList.add("bg-green-600")
    button.classList.remove("bg-purple-600")

    setTimeout(() => {
      button.textContent = originalText
      button.classList.remove("bg-green-600")
      button.classList.add("bg-purple-600")
    }, 2000)
  }

  function fallbackCopyTextToClipboard(text, button) {
    const textArea = document.createElement("textarea")
    textArea.value = text
    textArea.style.top = "0"
    textArea.style.left = "0"
    textArea.style.position = "fixed"

    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    try {
      document.execCommand("copy")
      showCopyFeedback(button)
    } catch (err) {
      console.error("Fallback: Oops, unable to copy", err)
    }

    document.body.removeChild(textArea)
  }
}

function initLazyLoading() {
  if ("IntersectionObserver" in window) {
    const imageObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target
            const src = img.getAttribute("data-src")

            if (src) {
              img.src = src
              img.removeAttribute("data-src")
              img.classList.remove("lazy")
              observer.unobserve(img)
            }
          }
        })
      },
      {
        rootMargin: "50px 0px",
        threshold: 0.01,
      },
    )

    const lazyImages = document.querySelectorAll("img[data-src]")
    lazyImages.forEach((img) => {
      img.classList.add("lazy")
      imageObserver.observe(img)
    })
  } else {
    const lazyImages = document.querySelectorAll("img[data-src]")
    lazyImages.forEach((img) => {
      const src = img.getAttribute("data-src")
      if (src) {
        img.src = src
        img.removeAttribute("data-src")
      }
    })
  }
}

function initAccessibility() {
  const skipLink = document.createElement("a")
  skipLink.href = "#main-content"
  skipLink.textContent = "Pular para o conteúdo principal"
  skipLink.className =
    "sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:z-50 focus:p-4 focus:bg-white focus:text-black"
  document.body.insertBefore(skipLink, document.body.firstChild)

  const focusableElements = document.querySelectorAll(
    'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])',
  )

  focusableElements.forEach((element) => {
    element.addEventListener("focus", function () {
      this.classList.add("focus-visible")
    })

    element.addEventListener("blur", function () {
      this.classList.remove("focus-visible")
    })
  })

  const announcer = document.createElement("div")
  announcer.setAttribute("aria-live", "polite")
  announcer.setAttribute("aria-atomic", "true")
  announcer.className = "sr-only"
  document.body.appendChild(announcer)

  window.announceToScreenReader = (message) => {
    announcer.textContent = message
    setTimeout(() => {
      announcer.textContent = ""
    }, 1000)
  }

  const forms = document.querySelectorAll("form")
  forms.forEach((form) => {
    const inputs = form.querySelectorAll("input, textarea, select")
    inputs.forEach((input) => {
      if (input.hasAttribute("required")) {
        const label = form.querySelector(`label[for="${input.id}"]`)
        if (label && !label.textContent.includes("*")) {
          label.innerHTML += ' <span aria-label="obrigatório">*</span>'
        }
      }
    })
  })

  const buttons = document.querySelectorAll("button:not([aria-label]):not([aria-labelledby])")
  buttons.forEach((button) => {
    if (!button.textContent.trim() && !button.getAttribute("aria-label")) {
      console.warn("Button without accessible name found:", button)
    }
  })

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      const openModal = document.querySelector(".modal.show")
      if (openModal) {
        const closeBtn = openModal.querySelector('[data-dismiss="modal"], .close, .modal-close')
        if (closeBtn) closeBtn.click()
      }

      const openDropdown = document.querySelector(".dropdown.open")
      if (openDropdown) {
        openDropdown.classList.remove("open")
      }
    }
  })
}

function initMobileSearch() {
  const searchBtn = document.querySelector('#mobile-bottom-nav a[href="#"]:nth-child(2)')
  const mobileSearchOverlay = document.createElement("div")

  // Create mobile search overlay
  mobileSearchOverlay.id = "mobile-search-overlay"
  mobileSearchOverlay.className = "fixed inset-0 bg-white z-50 hidden"
  mobileSearchOverlay.innerHTML = `
    <div class="flex items-center p-4 border-b">
      <button id="close-mobile-search" class="mr-4 text-gray-600">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M19 12H5m7-7l-7 7 7 7"/>
        </svg>
      </button>
      <div class="flex-1 flex items-center bg-gray-100 rounded-full py-2 px-4">
        <input
          type="search"
          placeholder="O que você procura?"
          class="flex-1 border-none bg-transparent outline-none text-base placeholder-gray-500"
          id="mobile-search-input"
          autofocus
          action="{{ routes.search_url }}"
          method="get"
          role="search"
        >
        <button type="submit" class="text-purple-main ml-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        </button>
      </div>
    </div>
    <div class="p-4">
      <p class="text-gray-500 text-sm">Digite para buscar produtos...</p>
    </div>
  `

  document.body.appendChild(mobileSearchOverlay)

  if (searchBtn) {
    searchBtn.addEventListener("click", (e) => {
      e.preventDefault()
      mobileSearchOverlay.classList.remove("hidden")
      document.body.style.overflow = "hidden"

      // Focus on input after a small delay to ensure it's visible
      setTimeout(() => {
        const input = document.getElementById("mobile-search-input")
        if (input) input.focus()
      }, 100)
    })
  }

  const closeBtn = document.getElementById("close-mobile-search")
  if (closeBtn) {
    closeBtn.addEventListener("click", () => {
      mobileSearchOverlay.classList.add("hidden")
      document.body.style.overflow = ""
    })
  }

  // Close on escape key
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !mobileSearchOverlay.classList.contains("hidden")) {
      mobileSearchOverlay.classList.add("hidden")
      document.body.style.overflow = ""
    }
  })
}

// ====================================
// GLOBAL ERROR HANDLING
// ====================================

window.addEventListener("error", (e) => {
  console.error("Global error:", e.error)
})

window.addEventListener("unhandledrejection", (e) => {
  console.error("Unhandled promise rejection:", e.reason)
})

// ====================================
// PERFORMANCE MONITORING
// ====================================

if ("performance" in window) {
  window.addEventListener("load", () => {
    setTimeout(() => {
      const perfData = performance.getEntriesByType("navigation")[0]
      console.log("Page load performance:", {
        domContentLoaded: perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
        loadComplete: perfData.loadEventEnd - perfData.loadEventStart,
        totalTime: perfData.loadEventEnd - perfData.fetchStart,
      })
    }, 0)
  })
}
